import { isFluctuating, isWind } from '@e2m-customer-portal/shared';
import {
  Button,
  Collapse,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  ListItemTextProps,
} from '@mui/material';
import { ExpandMoreIcon, FolderIcon, LinkIcon, PaymentIcon, PrognosisMenuIcon, WarningIcon } from 'assets/icons';
import clsx from 'clsx';
import { isPast, subSeconds } from 'date-fns';
import { LinkedSidebarMarketLocationFieldsFragment } from 'generated/graphql';
import { MouseEvent, ReactElement, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
import { makeStyles } from 'tss-react/mui-compat';
import { mostCompactName } from 'utils/most-compact-name';
import {
  marketLocationAnnualPlanning,
  marketLocationFeedIn,
  marketLocationForecast,
  marketLocationMasterData,
  marketLocationOutages,
  marketLocationRevenueReport,
  marketLocation as marketLocationRoute,
  optimizationGroups,
} from '../../routes';

const useStyles = makeStyles<void, 'selected' | 'open'>()((theme, _params, classes) => ({
  open: {},
  selected: {},
  item: {
    boxSizing: 'border-box',
    width: '280px',
    height: '60px',
    marginTop: '2px',
    marginLeft: '1.25rem',
    paddingLeft: '1rem',
    borderRadius: 0,
    borderTopLeftRadius: '0.5rem',
    borderBottomLeftRadius: '0.5rem',
    backgroundColor: theme.palette.background.default,
    '&:hover, &:focus-visible': {
      backgroundColor: theme.palette.primary[50],
      color: theme.palette.primary.main,
    },
    [`&.${classes.selected}`]: {
      backgroundColor: theme.palette.primary[50],
      color: theme.palette.primary.main,
      '&:hover, &:focus-visible': {
        backgroundColor: theme.palette.primary[50],
        color: theme.palette.primary.main,
      },
    },
  },
  label: {
    marginRight: theme.spacing(4),
    alignItems: 'start',
  },
  itemTextPrimary: {
    ...theme.typography.body3,
    color: 'inherit !important',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
    [`.${classes.selected} &`]: {
      color: theme.palette.primary.main,
    },
  },
  itemTextSecondary: {
    ...theme.typography.body2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  chevronContainer: {},
  chevron: {
    transition: theme.transitions.create('transform'),
    [`&.${classes.open}`]: {
      transform: 'rotateX(180deg)',
    },
  },
  subItem: {
    boxSizing: 'border-box',
    height: '50px',
    width: '100%',
    marginLeft: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    transition: theme.transitions.create(['color', 'fill']),
    '&:hover, &:focus-visible': {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.primary.main,
      fill: theme.palette.primary.main,
    },
    '&:not(:last-child)': {
      borderBottom: '1px solid #c4cdd7',
    },
    [`&.${classes.selected}`]: {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.primary.main,
      '&:hover, &:focus-visible': {
        backgroundColor: theme.palette.background.paper,
      },
    },
  },
  subItemTextPrimary: {
    ...theme.typography.body3,
    color: 'inherit !important',
    [`.${classes.selected} &`]: {
      color: theme.palette.primary.main,
    },
  },
  iconContainer: {
    minWidth: '40px',
  },
  icon: {
    fill: 'inherit',
    [`.${classes.selected} &`]: {
      fill: theme.palette.primary.main,
    },
  },
  list: {
    marginRight: theme.spacing(7),
  },
}));

export interface MarketLocationItemProps {
  /**
   * The marketLocation to display. Determines the links of the generated list items as well as the
   * label of the expandable item.
   */
  marketLocation: LinkedSidebarMarketLocationFieldsFragment;

  expanded: boolean;

  onChangeExpanded: (expanded: boolean) => void;
}

function useItemName(): (marketLocation: LinkedSidebarMarketLocationFieldsFragment) => ListItemTextProps {
  const [t] = useTranslation();

  return (marketLocation) => {
    const hasExternalName = marketLocation.externalName && marketLocation.externalName !== marketLocation.name;
    const isExpired = marketLocation.marketingEnd != null && isPast(marketLocation.marketingEnd);

    if (isExpired) {
      return {
        primary: hasExternalName
          ? marketLocation.externalName
          : mostCompactName(marketLocation.name, marketLocation.linkedMarketLocation?.name),
        secondary: (
          <>
            <WarningIcon width="14px" height="14px" />
            <span>{t('SIDEBAR_MARKET_LOCATION_EXPIRED', { date: subSeconds(marketLocation.marketingEnd, 1) })}</span>
          </>
        ),
        secondaryTypographyProps: {
          sx: (theme) => ({
            backgroundColor: theme.palette.background.paper,
            borderRadius: '4px',
            marginLeft: '-4px',
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
            '& span': {
              fontSize: '10px',
            },
            '& svg': {
              flexShrink: 0,
              marginRight: '1px',
              fill: 'currentColor',
            },
          }),
        },
      };
    }

    if (hasExternalName) {
      return {
        primary: marketLocation.externalName,
        secondary: marketLocation.name,
      };
    }

    return { primary: mostCompactName(marketLocation.name, marketLocation.linkedMarketLocation?.name) };
  };
}

function MarketLocationItemBase({
  marketLocation,
  expanded,
  onChangeExpanded,
}: Readonly<MarketLocationItemProps>): ReactElement<MarketLocationItemProps> {
  const [t] = useTranslation();
  const { pathname } = useLocation();
  const { classes } = useStyles();
  const getName = useItemName();

  const handleToggle = (event: MouseEvent): void => {
    event.stopPropagation();
    event.preventDefault();
    onChangeExpanded(!expanded);
  };

  const forecastLink = marketLocationFeedIn.getLink(marketLocation.id);
  const masterDataLink = marketLocationMasterData.getLink(marketLocation.id);
  const revenueReportLink = marketLocationRevenueReport.getLink(marketLocation.id);

  const params = { marketLocationId: marketLocation.id };
  const selected = marketLocationRoute.test(pathname, params, { end: false });
  const forecastSelected =
    marketLocationFeedIn.test(pathname, params) ||
    marketLocationForecast.test(pathname, params) ||
    marketLocationOutages.test(pathname, params) ||
    marketLocationAnnualPlanning.test(pathname, params) ||
    optimizationGroups.test(pathname, params);
  const masterDataSelected = marketLocationMasterData.test(pathname, params, { end: false });
  const revenueReportSelected = marketLocationRevenueReport.test(pathname, params, { end: false });

  return (
    <div data-testid={`sidebar-option-${marketLocation.id}`} role="option" aria-selected={selected}>
      <Button
        className={clsx(classes.item, { [classes.selected]: selected })}
        color={selected ? 'primary' : 'inherit'}
        startIcon={marketLocation.linkedMarketLocation ? <LinkIcon className="-mr-2 size-6" /> : undefined}
        component={Link}
        to={forecastLink}
        onClick={() => !expanded && onChangeExpanded(true)}
      >
        <ListItemText
          className={classes.label}
          classes={{
            primary: classes.itemTextPrimary,
            secondary: classes.itemTextSecondary,
          }}
          {...getName(marketLocation)}
        />
        <ListItemSecondaryAction className={classes.chevronContainer}>
          <IconButton data-testid="toggle-sub-menu" size="small" onClick={handleToggle}>
            <ExpandMoreIcon className={clsx(classes.chevron, { [classes.open]: expanded })} />
          </IconButton>
        </ListItemSecondaryAction>
      </Button>

      <Collapse data-testid="sub-menu" in={expanded} aria-expanded={expanded}>
        <List className={classes.list} disablePadding>
          {marketLocation.permissions.seeForecast && (
            <ListItemButton
              component={Link}
              className={classes.subItem}
              classes={{ selected: classes.selected }}
              selected={forecastSelected}
              to={forecastLink}
            >
              <ListItemIcon className={classes.iconContainer}>
                <PrognosisMenuIcon className={classes.icon} />
              </ListItemIcon>
              <ListItemText
                classes={{ primary: classes.subItemTextPrimary }}
                primary={
                  isFluctuating(marketLocation.technologies)
                    ? t('SIDEBAR_MARKET_LOCATION_WEEKLY_PLANNING')
                    : t('SIDEBAR_MARKET_LOCATION_FORECAST')
                }
              />
            </ListItemButton>
          )}

          <ListItemButton
            component={Link}
            className={classes.subItem}
            classes={{ selected: classes.selected }}
            selected={masterDataSelected}
            to={masterDataLink}
          >
            <ListItemIcon className={classes.iconContainer}>
              <FolderIcon className={classes.icon} />
            </ListItemIcon>
            <ListItemText
              classes={{ primary: classes.subItemTextPrimary }}
              primary={t('SIDEBAR_MARKET_LOCATION_MASTER_DATA')}
            />
          </ListItemButton>

          {marketLocation.permissions.seeRevenue && !isWind(marketLocation.technologies) && (
            <ListItemButton
              component={Link}
              className={classes.subItem}
              classes={{ selected: classes.selected }}
              selected={revenueReportSelected}
              to={revenueReportLink}
            >
              <ListItemIcon className={classes.iconContainer}>
                <PaymentIcon className={classes.icon} />
              </ListItemIcon>
              <ListItemText
                classes={{ primary: classes.subItemTextPrimary }}
                primary={t('SIDEBAR_MARKET_LOCATION_REVENUE_REPORT')}
              />
            </ListItemButton>
          )}
        </List>
      </Collapse>
    </div>
  );
}

export const MarketLocationItem = memo(MarketLocationItemBase);
